/**
 * Unified signing interface — routes signing operations based on wallet type.
 *
 * Relocated from frontend/src/lib/wallet/signing.ts as part of the auth
 * boundary migration (spec 009 FILE 14 Group E — `useSubmitTransaction`).
 *
 * - Embedded/imported wallets → client-side Turnkey signing (via the
 *   `turnkeySign*` functions threaded in through `SigningDeps`, which the
 *   already-relocated `useTurnkeySigningDeps` hook provides).
 * - External wallets → wallet provider `signMessage` / `signTransaction`.
 *
 * The frontend version read `useImpersonationStore` directly; the SDK owns
 * impersonation state (`src/auth/impersonation/`). The impersonation block is
 * an explicit `impersonationActive` parameter — the host / engine threads the
 * current snapshot. The logger is injected. Pure runtime — platform-neutral,
 * safe in src/auth/ core.
 */

import type { TxEngineLogger } from '../../tx-engine/config.js';
import type { SigningDeps } from './signing-types.js';

/**
 * Wrap a Turnkey signing failure without destroying its identity: the
 * original error rides on `cause`, and a `code` property (e.g.
 * `NO_SESSION_FOUND`, `SESSION_EXPIRED`) is copied onto the wrapper so the
 * tx-engine's `isTurnkeyErrorCode` probes still classify it — a dead signing
 * session must map to `TX_SESSION_EXPIRED`, not a generic sign failure.
 */
function wrapSigningError(prefix: string, err: unknown): Error {
  const msg = err instanceof Error ? err.message : String(err);
  const wrapped = new Error(`${prefix}: ${msg}`, { cause: err });
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === 'string') {
    (wrapped as Error & { code?: string }).code = code;
  }
  return wrapped;
}

/** Minimal wallet shape the signing runtime needs (type + address). */
export interface SigningWallet {
  type: string;
  address: string;
}

/** Returns which signing method will be used for a given wallet + deps. */
export function resolveSigningMethod(
  walletType: string,
  deps: SigningDeps,
): 'external' | 'turnkey-client' | 'turnkey-unavailable' {
  if (walletType === 'external') return 'external';
  if (deps.turnkeySignTransaction) return 'turnkey-client';
  return 'turnkey-unavailable';
}

/**
 * Sign an arbitrary message with the given wallet.
 * Returns the signature as a Uint8Array.
 *
 * @param impersonationActive — when true, refuses to sign (defence in depth;
 *   message signing is used for wallet-ownership proofs and must not run
 *   under an impersonation grant).
 */
export async function signMessage(
  wallet: SigningWallet,
  message: Uint8Array,
  deps: SigningDeps,
  logger: TxEngineLogger,
  impersonationActive: boolean,
  isDev: boolean,
): Promise<Uint8Array> {
  if (impersonationActive) {
    throw new Error('Message signing is blocked while an impersonation grant is active.');
  }

  const signingMethod = resolveSigningMethod(wallet.type, deps);
  if (isDev) {
    logger.info('[turnkey] signMessage routing', {
      walletType: wallet.type,
      walletAddress: wallet.address,
      signingMethod,
    });
  }
  if (wallet.type === 'external') {
    const provider = deps.getExternalProvider();
    if (provider === null) {
      throw new Error('External wallet provider not available');
    }
    const result = await provider.signMessage(message, 'utf8');
    return result.signature;
  }

  // Embedded or imported — client-side Turnkey signing
  if (deps.turnkeySignMessage) {
    try {
      return await deps.turnkeySignMessage(message, wallet.address);
    } catch (err: unknown) {
      throw wrapSigningError('Turnkey message signing failed', err);
    }
  }

  throw new Error('Turnkey signing unavailable — please refresh and try again');
}

/**
 * Sign a Solana transaction with the given wallet.
 * Returns the signed transaction bytes.
 *
 * @param impersonationActive — second-layer signing block. The engine's
 *   `submitTransaction` already short-circuits earlier; this is defence in
 *   depth for any caller that reaches signing directly.
 */
export async function signTransaction(
  wallet: SigningWallet,
  transaction: Uint8Array,
  deps: SigningDeps,
  logger: TxEngineLogger,
  impersonationActive: boolean,
  isDev: boolean,
): Promise<Uint8Array> {
  if (impersonationActive) {
    throw new Error('Transaction signing is blocked while an impersonation grant is active.');
  }

  const txSigningMethod = resolveSigningMethod(wallet.type, deps);
  if (isDev) {
    logger.info('[turnkey] signTransaction routing', {
      walletType: wallet.type,
      walletAddress: wallet.address,
      signingMethod: txSigningMethod,
    });
  }
  if (wallet.type === 'external') {
    const provider = deps.getExternalProvider();
    if (provider === null) {
      throw new Error('External wallet provider not available');
    }
    const result = await provider.signTransaction(transaction);
    return result.signature;
  }

  // Embedded or imported — client-side Turnkey signing
  if (deps.turnkeySignTransaction) {
    try {
      return await deps.turnkeySignTransaction(transaction, wallet.address);
    } catch (err: unknown) {
      throw wrapSigningError('Turnkey transaction signing failed', err);
    }
  }

  throw new Error('Turnkey signing unavailable — please refresh and try again');
}
